import { motion } from "framer-motion";
import { FiPlus } from "react-icons/fi";
import { RiDeleteBinLine } from "react-icons/ri";
import { TbLayoutSidebarLeftCollapse } from "react-icons/tb";
import { CiLogout } from "react-icons/ci";
import { ConversationLink } from "./ConversationLink";
import { Skeleton } from "@mui/material";

import { useNavigate } from "react-router-dom";
import { useGlobalContext } from "../../context/ConversationsProvider";
import useSidebarHook from "../../hooks/useSidebarHook";
import { useState } from "react";
import ClientDropdown from "./ClientDropdown";
import { DeleteDialog } from "../common/CommonModal";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { archiveAllConversationService } from "../../services/conversationServices";
import { useClient } from "../../context/ClientsProvider";
import { ACTIVE_CONVERSATION_QUERY_KEY } from "../../hooks/chat/constants";
import Loader from "../common/Loader";
import { Button, IconButton } from "../../design-system/components";

const SidebarMain = ({ isCollapsed, toggleDrawer, onCollapse }) => {
  const navigate = useNavigate();
  const { setIsChatStarted } = useGlobalContext();
  const { selectedClientId: clientId, shouldShowClientSelector } = useClient();
  const queryClient = useQueryClient();
  const { conversationList, isConversationListLoading, handleLogout } =
    useSidebarHook();
  // Landing path for the active client; falls back to root before the client
  // resolves (ClientsProvider then redirects "/" to "/:clientId").
  const clientHome = clientId ? `/${encodeURIComponent(clientId)}` : "/";
  const { mutateAsync: deleteAllConversations, isPending: isDeletingAll } =
    useMutation({
      mutationFn: async (clientId) => {
        await archiveAllConversationService(clientId);
      },
      onSuccess: async () => {
        await queryClient.invalidateQueries({
          queryKey: ["conversation-list", clientId],
        });
        // Every conversation is gone: drop any cached chat and return
        // the user to the landing page regardless of which chat was open,
        // otherwise the now-deleted conversation stays visible at /c/:id.
        queryClient.removeQueries({
          queryKey: [ACTIVE_CONVERSATION_QUERY_KEY],
        });
        setIsChatStarted(false);
        navigate(clientHome);
      },
    });
  const [isDeleteAllVisible, setIsDeleteAllVisible] = useState(false);

  const startNewChat = () => {
    navigate(clientHome);
    setIsChatStarted(false);
    if (toggleDrawer) {
      toggleDrawer(false)();
    }
  };

  return (
    <>
      <motion.div
        className="sidebar-main--container"
        initial={{ width: 0, opacity: 1, display: "flex" }}
        animate={{
          width: isCollapsed ? "0px" : "300px",
          opacity: isCollapsed ? 0 : 1,
          display: isCollapsed ? "none" : "flex",
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
      >
        <div className="sidebar-header--wrapper">
          <div className="sidebar-header-text--wrapper">
            <div className="copilot-logo--wrapper">
              <img src="/logo.svg" alt="" />
            </div>
            <h3>Portfolio Assistant</h3>
          </div>
          {(onCollapse || toggleDrawer) && (
            <IconButton
              label="Collapse sidebar"
              size="md"
              onClick={() => {
                if (toggleDrawer) {
                  toggleDrawer(false)();
                } else if (onCollapse) {
                  onCollapse();
                }
              }}
            >
              <TbLayoutSidebarLeftCollapse />
            </IconButton>
          )}
        </div>
        <div className="sidebar-body">
          <div className="sidebar-conversation--container">
            <button className="sidebar-newchat" onClick={startNewChat}>
              <span>New chat</span>
              <FiPlus aria-hidden="true" />
            </button>
            {isConversationListLoading ? (
              <>
                {[1, 2, 3, 4].map((elm) => (
                  <Skeleton
                    key={elm}
                    variant="rounded"
                    width="100%"
                    height={44}
                  />
                ))}
              </>
            ) : (
              [...conversationList]
                .reverse()
                .map((elm) => (
                  <ConversationLink
                    key={elm.id}
                    to={
                      clientId
                        ? `/${encodeURIComponent(clientId)}/c/${elm.id}`
                        : `/c/${elm.id}`
                    }
                    id={elm.id}
                    name={elm.name}
                    setIsChatStarted={setIsChatStarted}
                    toggleDrawer={toggleDrawer}
                  />
                ))
            )}
          </div>
          <div className="sidebar-actions--wrapper">
            <Button
              variant="destructive"
              size="sm"
              startIcon={<RiDeleteBinLine />}
              onClick={() => setIsDeleteAllVisible(true)}
            >
              Clear history
            </Button>
            {shouldShowClientSelector && (
              <Button
                variant="ghost"
                size="sm"
                startIcon={<CiLogout />}
                onClick={handleLogout}
              >
                Logout
              </Button>
            )}
          </div>
        </div>
        <div className="sidebar-site-footer">
          <ClientDropdown />
        </div>
      </motion.div>
      {isDeleteAllVisible && (
        <DeleteDialog
          isOpen={isDeleteAllVisible}
          handleClose={() => setIsDeleteAllVisible(false)}
          conversationList={conversationList}
          onDelete={async () => {
            setIsDeleteAllVisible(false);
            await deleteAllConversations(clientId);
          }}
        />
      )}
      {isDeletingAll && <Loader />}
    </>
  );
};

export default SidebarMain;
